import { NgStyle } from '@angular/common';
import { Component, OnDestroy, OnInit, computed } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CardSensitiveDetailsRendererService } from '@app/finxone-web-frontend/app/lib/services/card-management-service/card-sensitive-details-renderer.service';
import { CardManagementService } from '@app/finxone-web-frontend/app/lib/services/card-management-service/card-management-service';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { CardState } from '@app/finxone-web-frontend/app/lib/state/card.state';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import {
  BaseCssProperties,
  PaymentProvider,
  ProviderClass,
  UiZoneWidgetAttributeConfig,
  commonContentStyleList,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import {
  EMPTY,
  Subject,
  catchError,
  distinctUntilChanged,
  from,
  map,
  switchMap,
  takeUntil,
  throwError,
} from 'rxjs';
import { BaseComponent } from '../../base/base.component';

@Component({
  selector: 'finxone-web-frontend-card-pin-widget',
  templateUrl: './card-pin-widget.component.html',
  styleUrl: './card-pin-widget.component.scss',
  imports: [NgStyle],
})
export class CardPINWidgetComponent extends BaseComponent implements OnInit, OnDestroy {
  @AutoDestroy private destroy$: Subject<void> = new Subject<void>();
  public ePin: string;
  public iframeError?: string;
  public iframeHostId = '';
  private confirmation: string;

  private currentCardId = computed(() => this.store.selectSnapshot(CardState.getCurrentCardId));

  constructor(
    private activatedRoute: ActivatedRoute,
    private store: Store,
    private cardManagementService: CardManagementService,
    private cardSensitiveDetailsRendererService: CardSensitiveDetailsRendererService,
    private configService: ConfigService,
    private dataIn: ProviderClass<UiZoneWidgetAttributeConfig>,
  ) {
    super();
  }

  public isAirwallexProvider(): boolean {
    return (
      this.store.selectSnapshot((state: { projectSettings?: { provider?: string } }) => state.projectSettings?.provider) ===
      PaymentProvider.AIRWALLEX
    );
  }

  ngOnInit(): void {
    const suffix = (this.currentCardId() ?? 'card').replace(/[^a-zA-Z0-9_-]/g, '');
    this.iframeHostId = `awx-card-pin-${suffix}`;
    this.onGetQueryParamsAndEPIN();
    this.onGetConfigStyle();
  }

  ngOnDestroy(): void {
    void this.cardSensitiveDetailsRendererService.teardown();
  }

  private onGetConfigStyle() {
    if (this.dataIn != undefined) {
      this.attri = this.dataIn.attri ?? null;
      this.contentStyle = this.configService.getSpecificBaseCssStyle(this.attri, [
        ...commonContentStyleList,
        BaseCssProperties.TEXT_FONT_STYLE,
        BaseCssProperties.FONT_SIZE,
        BaseCssProperties.TEXT_BOLD,
        BaseCssProperties.TEXT_ITALIC,
        BaseCssProperties.TEXT_UNDERLINE,
      ]);
    }
  }

  private onGetQueryParamsAndEPIN() {
    this.activatedRoute.queryParams
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(),
        switchMap((paramMap) => {
          this.confirmation = paramMap?.['confirmation'];
          const claimType = paramMap?.['claimType'];

          if (this.confirmation) {
            if (claimType === 'getPin' && this.isAirwallexProvider()) {
              return this.cardManagementService.getSecureToken({ cardId: this.currentCardId() }).pipe(
                switchMap((token) =>
                  from(
                    this.cardSensitiveDetailsRendererService.renderAirwallexIframe(
                      {
                        provider: PaymentProvider.AIRWALLEX,
                        mode: 'provider-sdk',
                        providerSession: {
                          sdk: 'airwallex',
                          accessToken: token.token,
                          expiresAt: token.expiresAt,
                          iframeOrigin: token.iframeOrigin,
                        },
                        providerData: { cardId: token.providerCardId ?? this.currentCardId() },
                      },
                      'pin',
                      this.iframeHostId,
                      {
                        onFailure: (error) => {
                          this.iframeError = error instanceof Error ? error.message : String(error);
                        },
                      },
                    ),
                  ),
                ),
                map(() => ({ pin: '' })),
              );
            }
            if (claimType === 'getPin') {
              return this.cardManagementService.viewCardPin(
                { cardId: this.currentCardId() },
                this.confirmation,
              );
            } else {
              return this.cardManagementService.viewEPIN(this.currentCardId(), this.confirmation);
            }
          } else {
            return EMPTY;
          }
        }),
        map((ePin) => {
          if (this.isAirwallexProvider()) {
            return;
          }
          this.ePin = typeof ePin === 'string' ? ePin : ePin.pin;
        }),
        catchError((error) => {
          console.error('Error fetching EPIN:', error);
          return throwError(() => error);
        }),
      )
      .subscribe();
  }
}
